import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "motion/react";
import PopupModal from "./components/PopupModal";

const HomePage = ({ setMenuOpen, features }) => {
    const location = useLocation();
    const [showPopup, setShowPopup] = useState(false);

    const handleJoinWaitlist = () => {
        setShowPopup(true);
        if (window.gtag) {
            window.gtag("event", "join_waitlist_click", {
                event_category: "engagement",
                event_label: "Get Early Access Button",
            });
        }
    };

    useEffect(() => {
        if (location.hash) {
            const element = document.getElementById(location.hash.slice(1));
            if (element) {
                element.scrollIntoView({ behavior: "smooth" });
            }
        }
    }, [location]);
    return (
        <>
            {/* Hero Section */}
            <section
                id="hero"
                className="pt-16 min-h-screen flex flex-col items-center justify-center text-center p-6 relative text-[#333] overflow-hidden"
            >
                {/* Background image and overlay */}
                <img
                    src="demo.webp"
                    alt=""
                    className="absolute bottom-12 w-full object-bottom z-0 border-0 max-w-3xl mx-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#fbf5f3]/20 via-[#fbf5f3]/90 via-25% to-[#fbf5f3] z-10"></div>

                {/* Hero content */}
                <div className="relative z-20">
                    <motion.h1
                        className="text-5xl font-bold"
                        initial={{ opacity: 0, y: -50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        Simplify Your Messaging with AI
                    </motion.h1>
                    <motion.p
                        className="mt-4 text-lg"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        Autocomplete your text messages with AI-powered suggestions.
                    </motion.p>
                    <motion.div
                        className="mt-6 flex flex-col sm:flex-row gap-4 justify-center"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.8 }}
                    >
                        <button
                            onClick={handleJoinWaitlist}
                            className="px-6 py-3 bg-[#18a999] hover:bg-[#1e8e7e] rounded-md text-lg font-medium text-white"
                        >
                            Download Now
                        </button>
                        <a
                            href="https://github.com/coreply/coreply"
                            className="px-6 py-3 bg-gray-800 hover:bg-gray-700 rounded-md text-lg font-medium text-white"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Star on GitHub
                        </a>
                    </motion.div>
                </div>
                <motion.div
                    className="absolute bottom-12 text-3xl animate-bounce z-20"
                    onClick={(e) => {
                        e.preventDefault();
                        document.getElementById("features").scrollIntoView({ behavior: "smooth" });
                        setMenuOpen(false);
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8 }}
                >
                    ↓
                </motion.div>
            </section>
            <section id="features" className="p-6 bg-[#f2ebe6]">
                <motion.h2
                    className="text-4xl font-bold text-center mt-16"
                    initial={{ opacity: 0, y: -50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    Features
                </motion.h2>
                <div className="mt-8 flex flex-col gap-10">
                    {features.map((feature, index) => (
                        <motion.div
                            key={index}
                            className={`flex flex-col lg:flex-row ${
                                index % 2 !== 0 ? "lg:flex-row-reverse" : ""
                            } items-center justify-center`}
                            initial={{ opacity: 0, scale: 0.5 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="max-w-md p-4">
                                <h3 className="text-2xl font-bold text-center">{feature.title}</h3>
                                <p className="mt-4 text-lg text-center">{feature.description}</p>
                            </div>
                            <img
                                src={feature.src}
                                alt={feature.alt}
                                className="w-full h-auto object-cover rounded-xl max-w-md shadow-sm"
                            />
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="p-6 bg-[#fbf5f3]">
                <motion.h2
                    className="text-4xl font-bold text-center my-16"
                    initial={{ opacity: 0, y: -50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    Pricing
                </motion.h2>
                <div className="max-w-5xl mx-auto">
                    <div className="mt-8 flex flex-col md:flex-row gap-10 justify-center items-stretch">
                        <motion.div
                            className="bg-white p-6 rounded-xl text-center flex-1 flex flex-col"
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="flex-grow">
                                <h3 className="text-2xl font-bold">Free</h3>
                                <p className="mt-3 text-base">50 requests/month</p>
                                <p className="mt-3 text-base">Basic AI model</p>
                            </div>
                            <div className="mt-6">
                                <Link
                                    to="https://coreply.up.nadles.com/"
                                    className="px-6 py-3 bg-[#18a999] hover:bg-[#1e8e7e] rounded-md text-lg font-medium text-white inline-block"
                                >
                                    Start For Free
                                </Link>
                            </div>
                        </motion.div>
                        <motion.div
                            className="bg-white p-6 rounded-xl text-center flex-1 flex flex-col"
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="flex-grow">
                                <h3 className="text-2xl font-bold">Pay-As-You-Go</h3>
                                <p className="mt-3 text-base">$1 per 1000 requests ($0.001/request)</p>
                                <p className="mt-3 text-base">Advanced AI model</p>
                            </div>
                            <div className="mt-6">
                                <Link
                                    to="https://coreply.up.nadles.com/"
                                    className="px-6 py-3 bg-[#18a999] hover:bg-[#1e8e7e] rounded-md text-lg font-medium text-white inline-block"
                                >
                                    Get Your Key
                                </Link>
                            </div>
                        </motion.div>
                        <motion.div
                            className="bg-white p-6 rounded-xl text-center flex-1 flex flex-col"
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="flex-grow">
                                <h3 className="text-2xl font-bold">Business</h3>
                                <p className="mt-3 text-base">Custom Pricing and AI model</p>
                            </div>
                            <div className="mt-6">
                                <Link
                                    to="/#contact"
                                    className="px-6 py-3 bg-[#18a999] hover:bg-[#1e8e7e] rounded-md text-lg font-medium text-white inline-block"
                                >
                                    Get In Touch
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>
            <section id="contact" className="p-6 bg-[#f2ebe6]">
                <div className="min-h-screen pt-32 flex flex-col items-center p-6">
                    <iframe
                        src="https://docs.google.com/forms/d/e/1FAIpQLSdd_t6FEDtby9ihsBIaMxbn0kN_xfTQBEQGams7O5ZArX7p9A/viewform?embedded=true"
                        width="640"
                        height="900"
                        frameborder="0"
                        marginheight="0"
                        marginwidth="0"
                        className="max-w-full"
                        title="Contact Form"
                    >
                        Loading…
                    </iframe>
                </div>
            </section>

            {/* Popup Modal */}
            {showPopup && <PopupModal onClose={() => setShowPopup(false)} />}
        </>
    );
};
export default HomePage;
